"use client";

import React, { useEffect, useRef, useState } from "react";
import { SquareCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const services = [{
  title: "General Pest Control",
  description: "Comprehensive interior and exterior treatments to keep common Florida pests out of your home year-round."
}, {
  title: "Termite Treatment",
  description: "Advanced baiting and liquid defense systems to protect your property's structural integrity from silent destroyers."
}, {
  title: "Mosquito Control",
  description: "Targeted yard treatments that significantly reduce mosquito populations, letting you enjoy your outdoor spaces again."
}, {
  title: "Spider Control",
  description: "Thorough web removal and barrier applications to keep dangerous and nuisance spiders away from your living areas."
}, {
  title: "Roach Extermination",
  description: "Fast-acting and long-lasting solutions to eliminate cockroach infestations at their source."
}, {
  title: "Ant Management",
  description: "Strategic baiting techniques that destroy ant colonies, preventing them from marching through your kitchen."
}];
export default function PestControlFeatures() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: valueRef,
    isVisible: isValueVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="pestcontrolfeatures" className="bg-background py-16 md:py-24 overflow-hidden">
            <div className="container mx-auto px-4 md:px-6">
                
                {}
                <div ref={headerRef as React.RefObject<HTMLDivElement>} className={cn("text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
                    <h2 className="font-default text-4xl md:text-5xl font-bold text-foreground mb-6 tracking-tight">
                        Reliable Solutions for Every Pest Problem
                    </h2>
                    <p className="font-serif text-lg text-muted-foreground">
                        Peace River Pest Control provides targeted, effective treatments designed specifically for the unique challenges of Southwest Florida.
                    </p>
                </div>

                {}
                <div className="grid grid-cols-12 gap-6 md:gap-8">
                    {services.map((service, index) => <ServiceCard key={index} service={service} index={index} />)}
                </div>

                {}
                <div ref={valueRef as React.RefObject<HTMLDivElement>} className={cn("mt-20 bg-foreground text-background p-8 md:p-12 border-l-8 border-primary transition-all duration-1000 ease-out", isValueVisible ? "opacity-100 scale-100" : "opacity-0 scale-95")}>
                    <div className="max-w-4xl mx-auto text-center">
                        <h3 className="font-default text-3xl md:text-4xl font-bold mb-6 text-primary">
                            Dedicated to North Fort Myers & Beyond
                        </h3>
                        <p className="font-serif text-lg md:text-xl leading-relaxed opacity-90">
                            As a fully insured and FDACS Licensed operator, we pride ourselves on delivering unmatched reliability and rapid response times across North Fort Myers, Punta Gorda, Port Charlotte, Cape Coral, and Burnt Store. When you need us, we're already in the neighborhood.
                        </p>
                    </div>
                </div>

                {}
                <div className="mt-16 flex justify-center">
                    <Button asChild size="lg" className="rounded-full font-default text-lg px-10 py-7 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
                        <Link to="/ContactPage">
                            Get A Free Quote
                        </Link>
                    </Button>
                </div>

            </div>
        </section>;
}
function ServiceCard({
  service,
  index
}: {
  service: typeof services[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <div ref={ref as React.RefObject<HTMLDivElement>} data-index={index} className={cn("col-span-12 md:col-span-6 lg:col-span-4 transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")} style={{
    transitionDelay: `${index * 100}ms`
  }}>
            <Card className="h-full rounded-none border-2 border-border/10 shadow-none hover:border-primary/50 hover:shadow-md transition-all duration-300 bg-card">
                <CardHeader className="pb-4">
                    <div className="mb-4 inline-flex items-center justify-center size-12 bg-primary/10 text-primary">
                        <SquareCheck className="size-6" />
                    </div>
                    <CardTitle className="font-default text-2xl font-bold text-foreground">
                        {service.title}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="font-serif text-muted-foreground leading-relaxed">
                        {service.description}
                    </p>
                </CardContent>
            </Card>
        </div>;
}